import { pause } from "@src/Utils";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import Card, { ExpandableCard } from "./Card";
import CheckBox, { MasterCheckBox } from "./CheckBox";
import StudyIndicator from "./StudyIndicator";

/**
 * @typedef {{
 *   participantId: string | number;
 *   fullName: string;
 *   email: string;
 *   dateJoined: string;
 *   preTest: string;
 *   training: string;
 *   postTest: string;
 *   postDrivingTest: string;
 *   progress: number;
 *   lastActivity: string;
 *   study: string;
 *   role: string;
 * }} UserCardInfo
 *
 * @param {{
 *   user: UserCardInfo;
 *   selected: boolean;
 *   visible: boolean;
 *   onToggleSelected: function;
 *   hideOnDeselect: boolean;
 * }} props
 */
const UserListCard = (props) => {
  const { user, selected, visible, onToggleSelected, hideOnDeselect } = props;

  /** @type {UseState<UserCardInfo>} */
  const [parsedUser, setParsedUser] = useState(null);

  const deselected = hideOnDeselect && !selected;

  useEffect(() => {
    if (!visible) return;
    if (user) setParsedUser({ ...user });
  }, [user, visible]);

  useEffect(() => {
    if (deselected) {
      setParsedUser(null);
    } else if (hideOnDeselect) {
      pause(200).then(() => setParsedUser({ ...user }));
    }
  }, [selected]);

  function colorTask(task) {
    return task === "Not Activated" ? "red" : task && "green";
  }

  return (
    <ExpandableCard
      className={clsx("user-list-card", deselected && "hidden")}
      loading={!parsedUser}
      visible={visible}
      inert={deselected}
      data={parsedUser}
      header={
        <>
          <CheckBox
            state={[selected, () => onToggleSelected?.(user, selected)]}
            variant="dark"
          />
          <span className="user-name">{parsedUser?.fullName}</span>
          <span className="user-pid">{parsedUser?.participantId}</span>
          <span className="user-study">
            <StudyIndicator role={parsedUser?.role} />
          </span>
          <span className="user-progress">{parsedUser?.progress}</span>
        </>
      }
    >
      <InfoRow k="Name" v={parsedUser?.fullName} />
      <InfoRow k="Email" v={parsedUser?.email} />
      <InfoRow k="Study" v={parsedUser?.study} />
      <InfoRow k="Joined on" v={parsedUser?.dateJoined} />

      <InfoRow
        k="Training"
        v={parsedUser?.training || "N/A"}
        className={colorTask(parsedUser?.training)}
      />
      <InfoRow
        k="Quiz 1"
        v={parsedUser?.preTest || "N/A"}
        className={colorTask(parsedUser?.preTest)}
      />
      <InfoRow
        k="Quiz 2"
        v={parsedUser?.postTest || "N/A"}
        className={colorTask(parsedUser?.postTest)}
      />
      <InfoRow
        k="Quiz 3"
        v={parsedUser?.postDrivingTest || "N/A"}
        className={colorTask(parsedUser?.postDrivingTest)}
      />
    </ExpandableCard>
  );
};

/** @param {React.ComponentProps<MasterCheckBox>} props */
export const UserListHeader = (props) => {
  return (
    <Card className="user-list-header" bgColor="var(--list-header-color)">
      <div className="data-cols">
        <MasterCheckBox variant="dark" {...props} />

        <span className="user-name">Full Name</span>
        <span className="user-pid">Participant ID</span>
        <span className="user-study">Study</span>
        <span className="user-progress">Progress</span>
        <IconChevronDown
          className="expand-arrow"
          size="2rem"
          visibility="hidden"
        />
      </div>
    </Card>
  );
};

/** @param {{ k: string; v: string; className: string }} */
const InfoRow = ({ k, v, className }) => (
  <div className={clsx("info-row", className)}>
    {k && <span className="info-key">{k}:</span>}
    <span className="info-value">{`${v}`}</span>
  </div>
);

export default UserListCard;
